import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Share2, 
  Facebook, 
  Twitter, 
  Linkedin, 
  Instagram,
  Link2,
  Check,
  X
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const ShareButtons = ({ 
  url, 
  title, 
  description,
  variant = 'default', // 'default', 'compact', 'expanded'
  showLabel = true,
  className = ''
}) => {
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = url || window.location.href;
  const shareTitle = title || 'String Sauce - Premium String Conditioner';
  const shareDescription = description || 'Check out String Sauce - the premium string conditioner for musicians!';

  const shareLinks = [
    {
      name: 'Facebook',
      icon: Facebook,
      color: 'hover:bg-blue-600/20 hover:text-blue-400',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    },
    {
      name: 'Twitter',
      icon: Twitter,
      color: 'hover:bg-sky-500/20 hover:text-sky-400',
      url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      color: 'hover:bg-blue-700/20 hover:text-blue-500',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    },
    {
      name: 'Instagram',
      icon: Instagram,
      color: 'hover:bg-pink-500/20 hover:text-pink-400',
      // Instagram doesn't have direct URL sharing, so we'll copy and prompt
      action: () => {
        copyToClipboard();
        toast.success('Link copied! Share it on Instagram');
      },
    },
  ];

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const handleShare = (link) => {
    if (link.action) {
      link.action();
    } else {
      window.open(link.url, '_blank', 'width=600,height=400');
    }
    setShowShareMenu(false);
  };

  // Native share API for mobile
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareDescription,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          setShowShareMenu(true);
        }
      }
    } else {
      setShowShareMenu(true);
    }
  };

  if (variant === 'expanded') {
    // Always visible horizontal buttons
    return (
      <div className={`flex flex-wrap items-center gap-2 ${className}`}>
        {showLabel && (
          <span className="text-sm text-zinc-400 mr-2">Share:</span>
        )}
        {shareLinks.map((link) => (
          <button
            key={link.name}
            onClick={() => handleShare(link)}
            className={`p-2 rounded-lg bg-zinc-800/50 border border-zinc-700/50 text-zinc-400 transition-all ${link.color}`}
            title={`Share on ${link.name}`}
            data-testid={`share-${link.name.toLowerCase()}-btn`}
          >
            <link.icon className="w-4 h-4" />
          </button>
        ))}
        <button
          onClick={copyToClipboard}
          className={`p-2 rounded-lg bg-zinc-800/50 border border-zinc-700/50 transition-all ${
            copied 
              ? 'bg-green-500/20 text-green-400 border-green-500/30' 
              : 'text-zinc-400 hover:bg-orange-500/20 hover:text-orange-400'
          }`}
          title="Copy link"
          data-testid="share-copy-btn"
        >
          {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
        </button>
      </div>
    );
  }

  // Default: Button with dropdown menu
  return (
    <div className={`relative ${className}`}>
      <Button
        variant="outline"
        size={variant === 'compact' ? 'sm' : 'default'}
        onClick={handleNativeShare}
        className="gap-2 border-zinc-700 hover:border-orange-500/50 hover:bg-orange-500/10"
        data-testid="share-button"
      >
        <Share2 className="w-4 h-4" />
        {showLabel && 'Share'}
      </Button>

      <AnimatePresence>
        {showShareMenu && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40"
              onClick={() => setShowShareMenu(false)}
            />
            
            {/* Share menu */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              className="absolute right-0 top-full mt-2 z-50 bg-zinc-900 border border-zinc-700 rounded-xl shadow-xl p-3 min-w-[200px]"
            >
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-zinc-700">
                <span className="text-sm font-medium text-white">Share via</span>
                <button
                  onClick={() => setShowShareMenu(false)}
                  className="p-1 hover:bg-zinc-800 rounded-lg text-zinc-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              <div className="space-y-1">
                {shareLinks.map((link) => (
                  <button
                    key={link.name}
                    onClick={() => handleShare(link)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-zinc-300 transition-all ${link.color}`}
                    data-testid={`share-menu-${link.name.toLowerCase()}-btn`}
                  >
                    <link.icon className="w-4 h-4" />
                    <span className="text-sm">{link.name}</span>
                  </button>
                ))}
                
                <button
                  onClick={copyToClipboard}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${
                    copied 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'text-zinc-300 hover:bg-orange-500/20 hover:text-orange-400'
                  }`}
                  data-testid="share-menu-copy-btn"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                  <span className="text-sm">{copied ? 'Copied!' : 'Copy link'}</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ShareButtons;
